import { memo, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ChevronDown, Download, Loader2, Paperclip } from 'lucide-react';
import { cn, downloadUrl } from '@/lib/utils';
import { formatBytes } from '@/lib/format';
import { useOptionalVmClient } from '@/api/context';
import { useSmoothText } from '@/hooks/useSmoothText';
import type {
  TimelineItem,
  UserTimelineItem,
  AgentTimelineItem,
  CodeTimelineItem,
} from '@/hooks/useChatStore';
import type {
  SubagentTimelineItem,
  SubagentConversationTurn,
  SystemTimelineItem,
  DownloadTimelineItem,
} from '@/hooks/useChat';
import { AuthRequestCard } from '@/components/ChatPage/cards/AuthRequestCard';
import {
  PAPER_CHROME_RADIUS_CLASS,
  PAPER_ICON_BUTTON_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_MICRO_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from './paperShapes';

interface PaperMessageCardProps {
  item: TimelineItem;
  sessionId: string;
  onAuthComplete?: (sessionId: string, provider: string) => void;
}

const PaperUserCard = memo(({ item }: { item: UserTimelineItem }) => (
  <div className="flex justify-end">
    <div
      className={cn(
        'max-w-[75%] py-[10px] px-[14px]',
        'bg-[var(--periwinkle-wash)] text-[var(--fg)]',
        'border border-solid border-[var(--periwinkle-veil)]',
        'text-[14px] leading-[1.5] break-words overflow-hidden',
      )}
      style={{ borderRadius: '14px 14px 4px 14px' }}
    >
      <span className="whitespace-pre-wrap">{item.text}</span>
      {item.attachments && item.attachments.length > 0 && (
        <div className="mt-[8px] flex flex-col gap-[4px]">
          {item.attachments.map((att, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-[6px] text-[10px] text-[var(--fg-3)]"
            >
              <Paperclip className={PAPER_ICON_MICRO_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              <span className="truncate">{att.filename}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
));
PaperUserCard.displayName = 'PaperUserCard';

const PaperAgentCard = memo(({ item }: { item: AgentTimelineItem }) => {
  const { text: streamedText } = useSmoothText(item.text);
  return (
  <div
    className={cn(
      'text-[14px] leading-[1.55] text-[var(--fg-2)]',
      'max-w-none',
      // Block-level rhythm: paragraphs and lists get a 14px gap,
      // headings get extra room above (so they read as section
      // breaks) and a smaller gap to the body that follows. The
      // first-child resets keep the message from starting with
      // wasted top space.
      '[&_p]:m-0 [&_p]:mb-[14px] [&_p:last-child]:mb-0',
      '[&_ul]:m-0 [&_ul]:mb-[14px] [&_ul]:pl-[18px]',
      '[&_ol]:m-0 [&_ol]:mb-[14px] [&_ol]:pl-[18px]',
      '[&_li]:mb-[4px] [&_li]:text-[var(--fg-2)]',
      '[&_h1]:mt-[24px] [&_h1]:mb-[10px] [&_h1:first-child]:mt-0',
      '[&_h2]:mt-[24px] [&_h2]:mb-[10px] [&_h2:first-child]:mt-0',
      '[&_h3]:mt-[20px] [&_h3]:mb-[8px]  [&_h3:first-child]:mt-0',
      '[&_h4]:mt-[18px] [&_h4]:mb-[6px]  [&_h4:first-child]:mt-0',
      '[&_h5]:mt-[16px] [&_h5]:mb-[6px]  [&_h5:first-child]:mt-0',
      '[&_h6]:mt-[16px] [&_h6]:mb-[6px]  [&_h6:first-child]:mt-0',
      '[&_hr]:my-[16px] [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-[var(--hair)]',
      '[&_blockquote]:my-[14px] [&_blockquote]:border-l-2 [&_blockquote]:border-solid [&_blockquote]:border-[var(--hair)] [&_blockquote]:pl-[12px] [&_blockquote]:text-[var(--fg-3)]',
      '[&_b]:text-[var(--fg)] [&_strong]:text-[var(--fg)]',
      '[&_a]:text-[var(--periwinkle)] [&_a:hover]:underline',
      '[&_code]:bg-[var(--bg-alt)] [&_code]:px-[4px] [&_code]:py-[1px] [&_code]:rounded-[4px] [&_code]:text-[12px]',
      '[&_code]:font-[var(--font-mono-paper)]',
      '[&_pre]:bg-[var(--surface)] [&_pre]:border [&_pre]:border-solid [&_pre]:border-[var(--hair)]',
      '[&_pre]:rounded-lg [&_pre]:p-[12px] [&_pre]:overflow-x-auto [&_pre]:my-[14px]',
      '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[12px]',
    )}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
      {streamedText}
    </ReactMarkdown>
  </div>
  );
});
PaperAgentCard.displayName = 'PaperAgentCard';

const CODE_EXPANDED_STORAGE_KEY = 'agentica.paper.codeExpanded';
const MAX_EXPANDED_PER_SESSION = 200;

type ExpandedMap = Record<string, string[]>;

const readExpandedMap = (): ExpandedMap => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CODE_EXPANDED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: ExpandedMap = {};
    for (const [sid, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(ids)) out[sid] = ids.filter((v): v is string => typeof v === 'string');
    }
    return out;
  } catch {
    return {};
  }
};

const writeExpandedMap = (map: ExpandedMap): void => {
  try {
    window.localStorage.setItem(CODE_EXPANDED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota exceeded / storage disabled — ignore */
  }
};

const isExpandedPersisted = (sessionId: string, itemId: string): boolean => {
  const arr = readExpandedMap()[sessionId];
  return Array.isArray(arr) && arr.includes(itemId);
};

const setExpandedPersisted = (sessionId: string, itemId: string, expanded: boolean): void => {
  const map = readExpandedMap();
  const arr = map[sessionId] ?? [];
  const idx = arr.indexOf(itemId);
  if (expanded) {
    if (idx === -1) {
      arr.push(itemId);
      // LRU-ish cap: drop oldest insertions if over the per-session cap.
      if (arr.length > MAX_EXPANDED_PER_SESSION) {
        arr.splice(0, arr.length - MAX_EXPANDED_PER_SESSION);
      }
    }
  } else if (idx !== -1) {
    arr.splice(idx, 1);
  }
  if (arr.length > 0) map[sessionId] = arr;
  else delete map[sessionId];
  writeExpandedMap(map);
};

/**
 * Remove storage entries for sessions not in `knownSessionIds`. Called by
 * PaperChatLayout whenever the session list changes so expand state for
 * deleted sessions doesn't accumulate indefinitely.
 *
 * Caller must guard against transient empty states; passing an empty set
 * would wipe everything.
 */
export const pruneCodeCollapsedToSessions = (knownSessionIds: Set<string>): void => {
  if (typeof window === 'undefined') return;
  const map = readExpandedMap();
  let changed = false;
  for (const sid of Object.keys(map)) {
    if (!knownSessionIds.has(sid)) {
      delete map[sid];
      changed = true;
    }
  }
  if (changed) writeExpandedMap(map);
};

const PaperCodeCard = memo(({ item, sessionId }: { item: CodeTimelineItem; sessionId: string }) => {
  const [collapsed, setCollapsedState] = useState<boolean>(
    () => !isExpandedPersisted(sessionId, item.id),
  );
  const setCollapsed = (next: boolean) => {
    setCollapsedState(next);
    setExpandedPersisted(sessionId, item.id, !next);
  };
  const codeLineCount = item.code ? item.code.split('\n').length : 0;
  const bodyId = `paper-code-card-body-${item.id}`;
  return (
    <div
      className={cn(
        'bg-[var(--surface)] border border-solid border-[var(--hair)] rounded-lg overflow-hidden',
      )}
    >
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        className={cn(
          'w-full flex items-center justify-between gap-[8px] py-[6px] px-[12px]',
          'bg-transparent border-0 cursor-pointer text-left',
          'text-[9px] tracking-[0.14em] uppercase text-[var(--fg-3)]',
          'transition-colors duration-150 hover:bg-[var(--bg-alt)]',
          !collapsed && 'border-b border-solid border-[var(--hair)]',
        )}
      >
        <span className="flex items-center gap-[8px]">
          <ChevronDown
            className={cn(
              PAPER_ICON_TINY_CLASS,
              'shrink-0 transition-transform duration-150',
              collapsed && '-rotate-90',
            )}
            strokeWidth={PAPER_ICON_STROKE_WIDTH}
          />
          <span>Python</span>
          {collapsed && codeLineCount > 0 && (
            <span className="text-[var(--fg-4)] normal-case tracking-normal">
              · {codeLineCount} {codeLineCount === 1 ? 'line' : 'lines'}
            </span>
          )}
        </span>
        <span
          className={cn(
            'tracking-[0.14em] uppercase',
            item.status === 'running' && 'text-[var(--periwinkle)]',
            item.status === 'error' && 'text-[#F58C7A]',
            item.status === 'success' && 'text-[var(--fg-4)]',
          )}
        >
          {item.status}
        </span>
      </button>
      <div id={bodyId} hidden={collapsed}>
        {item.code && (
          <div
            className={cn(
              'text-[11px] leading-[1.5]',
              '[&_pre]:m-0 [&_pre]:px-[12px] [&_pre]:py-[10px] [&_pre]:overflow-x-auto [&_pre]:bg-transparent',
              '[&_code]:block [&_code]:whitespace-pre [&_code]:text-[11px] [&_code]:leading-[1.5]',
            )}
            style={{ fontFamily: 'var(--font-mono-paper)' }}
          >
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {'```python\n' + item.code + '\n```'}
            </ReactMarkdown>
          </div>
        )}
        {item.output && (
          <>
            <div className="border-t border-solid border-[var(--hair-subtle)]" />
            <pre
              className={cn(
                'm-0 px-[12px] py-[10px] overflow-x-auto text-[11px] leading-[1.5]',
                item.status === 'error' ? 'text-[#F58C7A]' : 'text-[var(--fg-3)]',
              )}
              style={{ fontFamily: 'var(--font-mono-paper)' }}
            >
              <code>{item.output}</code>
            </pre>
          </>
        )}
      </div>
    </div>
  );
});
PaperCodeCard.displayName = 'PaperCodeCard';

const PaperSubagentCard = memo(({ item }: { item: SubagentTimelineItem }) => {
  const [expanded, setExpanded] = useState(false);
  const bodyId = `paper-subagent-body-${item.agentId}`;
  const dotClass =
    item.status === 'running'
      ? 'bg-[var(--periwinkle)]'
      : item.status === 'error'
        ? 'bg-[var(--fg-3)]'
        : 'bg-[var(--fg-4)]';
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[10px]',
        'bg-[var(--surface)] border border-solid border-[var(--hair)]',
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className={cn(
          'flex w-full items-center gap-[8px] px-[12px] py-[8px] text-left',
          'text-[11px] text-[var(--fg-2)] hover:bg-[var(--bg-alt)] transition-colors',
        )}
      >
        <span className={cn('h-[6px] w-[6px] rounded-full shrink-0', dotClass)} />
        <span className="flex-1 truncate">
          Sub-agent: {item.task.slice(0, 80)}
          {item.task.length > 80 ? '…' : ''}
        </span>
        <span className="text-[10px] text-[var(--fg-3)]">{item.status}</span>
      </button>
      <div id={bodyId} hidden={!expanded}>
        {item.conversationTurns.length > 0 && (
          <PaperSubagentLog turns={item.conversationTurns} />
        )}
      </div>
    </div>
  );
});
PaperSubagentCard.displayName = 'PaperSubagentCard';

const PaperSubagentLog = ({ turns }: { turns: SubagentConversationTurn[] }) => (
  <div className="border-t border-solid border-[var(--hair)] px-[12px] py-[8px] flex flex-col gap-[6px]">
    {turns.map((turn, i) => (
      <div
        key={i}
        className={cn(
          'text-[11px] leading-[1.45]',
          turn.role === 'system' ? 'text-[var(--fg-3)]' : 'text-[var(--fg-2)]',
        )}
      >
        <span className="mr-[4px] text-[9px] uppercase tracking-[0.08em] text-[var(--fg-4)]">
          {turn.role}:
        </span>
        {turn.text.slice(0, 500)}
        {turn.text.length > 500 ? '…' : ''}
      </div>
    ))}
  </div>
);

const PaperSystemCard = memo(({ item }: { item: SystemTimelineItem }) => (
  <div className="flex justify-center">
    <span
      className={cn(
        'rounded-full px-[12px] py-[4px] text-[10px] text-[var(--fg-3)]',
        'bg-[var(--surface-2)] border border-solid border-[var(--hair)]',
      )}
    >
      {item.text}
    </span>
  </div>
));
PaperSystemCard.displayName = 'PaperSystemCard';

const PaperDownloadCard = memo(({ item }: { item: DownloadTimelineItem }) => {
  const vmClient = useOptionalVmClient();
  const [busy, setBusy] = useState(false);
  const url = vmClient?.getDownloadUrl(item.downloadId);
  const title = item.label?.trim() ? item.label : item.filename;
  const showFilename = title !== item.filename;

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!url || busy) return;
      setBusy(true);
      try {
        await downloadUrl(url, item.filename);
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer');
      } finally {
        setBusy(false);
      }
    },
    [url, item.filename, busy],
  );

  if (!url) return null;

  return (
    <a
      href={url}
      download={item.filename}
      onClick={handleClick}
      className={cn(
        'flex items-center gap-[12px] px-[14px] py-[10px]',
        PAPER_CHROME_RADIUS_CLASS,
        'bg-[var(--surface)] border border-solid border-[var(--hair)]',
        'hover:bg-[var(--bg-alt)] transition-colors',
      )}
    >
      <div
        className={cn(
          'h-[34px] w-[34px] shrink-0',
          PAPER_ICON_BUTTON_CLASS,
          'bg-[var(--periwinkle-wash)] text-[var(--periwinkle)]',
        )}
      >
        {busy ? (
          <Loader2 className={cn(PAPER_ICON_CLASS, 'animate-spin')} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
        ) : (
          <Download className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-medium text-[var(--fg)]">{title}</div>
        {showFilename && (
          <div className="truncate text-[10px] text-[var(--fg-3)]">{item.filename}</div>
        )}
        <div className="text-[10px] text-[var(--fg-3)]">{formatBytes(item.size)}</div>
      </div>
    </a>
  );
});
PaperDownloadCard.displayName = 'PaperDownloadCard';

export const PaperMessageCard = memo((props: PaperMessageCardProps) => {
  switch (props.item.kind) {
    case 'user':
      return <PaperUserCard item={props.item} />;
    case 'agent':
      return <PaperAgentCard item={props.item} />;
    case 'code':
      return <PaperCodeCard item={props.item} sessionId={props.sessionId} />;
    case 'subagent':
      return <PaperSubagentCard item={props.item} />;
    case 'system':
      return <PaperSystemCard item={props.item} />;
    case 'download':
      return <PaperDownloadCard item={props.item} />;
    case 'auth_request':
      return (
        <AuthRequestCard
          item={props.item}
          sessionId={props.sessionId}
          onAuthComplete={props.onAuthComplete}
        />
      );
    default:
      return null;
  }
});
PaperMessageCard.displayName = 'PaperMessageCard';
